import { Breadcrumb } from "@twinkltech/curriculum-architect-component-library";
import { GenericSubject } from "@twinkltech/curriculum-architect-domain-hooks";
import { useTwinklAnalytics } from "@twinkltech/web-analytics-ts";
import {
  Button,
  IconActionsOpenInNew,
  IconAlertsCheckCircle,
  Text,
} from "@twinkltech/web-ui";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { SubjectHelper } from "@/utils/SubjectHelper/SubjectHelper";

import { paths } from "@/paths";

import { StyledTag } from "../StyledTag/StyledTag";

import { EXPLORE_SCHEME_HEADER_NS } from "./ExploreSchemeHeader.i18n";

export const ExploreSchemeHeader = ({ scheme }: { scheme: GenericSubject }) => {
  const { t } = useTranslation(EXPLORE_SCHEME_HEADER_NS);

  const { click } = useTwinklAnalytics();

  const schemeLabel = SubjectHelper.getLabel({ slug: scheme?.slug });
  const canUserAccess = scheme?.userHasAccess === true;

  const schemeImage =
    SubjectHelper.getCardDecorations(scheme?.slug).imageUrl || "";

  return (
    <>
      <Breadcrumb className="mb-6">
        <Breadcrumb.Item>
          <Link to={paths.EXPLORE_SCHEME_LIST_CONTENTS_PATH}>
            <span className="truncate max-w-[200px] block">
              {t("terms.explore-scheme-content")}
            </span>
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item current className="text-gray-500">
          <span className="truncate max-w-[200px] block">{schemeLabel}</span>
        </Breadcrumb.Item>
      </Breadcrumb>

      {/* Scheme Details */}
      <div
        aria-label={t("scheme-details.title")}
        className={[
          "bg-neutral-100 p-5 border-b-8 flex gap-6 mb-10 border-b-blue-500 rounded-t-xl",
        ]
          .filter(Boolean)
          .join(" ")}
        role="region"
      >
        {schemeImage !== "" && (
          <div className="max-w-[60px]">
            <img role="presentation" src={schemeImage} />
          </div>
        )}
        <div className="flex flex-col gap-2 grow">
          <div className="max-w-[800px] flex self-start gap-3">
            <Text as="h1" variant="2xl-medium">
              <span className="line-clamp-2">{schemeLabel}</span>
            </Text>

            <div className="mt-auto ml-auto flex gap-2">
              {canUserAccess ? (
                <StyledTag size="sm" variant="green">
                  <IconAlertsCheckCircle className="text-green-500 mr-1 -my-1" />
                  Included in Membership
                </StyledTag>
              ) : (
                SubjectHelper.getSubjectAddOnTags(scheme?.slug).map((tag) => (
                  <StyledTag key={tag.label} size="sm" variant={tag.variant}>
                    {t(`tags.${tag.label}`)}
                  </StyledTag>
                ))
              )}
            </div>
          </div>
          <div className="flex gap-3 justify-between">
            <Text as="p" className="max-w-[626px]">
              {SubjectHelper.getDescription({
                slug: scheme?.slug,
              })}
            </Text>
            {canUserAccess ? (
              <Button
                as={Link}
                className="self-end"
                to={paths.CURRICULUM_LIST_PATH}
                variant="secondary"
              >
                {t("cta.plan-scheme")}
              </Button>
            ) : (
              <Button
                as={Link}
                className="self-end"
                target="_blank"
                to={`${paths.external.TWINKL_URL}${SubjectHelper.getExploreLink(
                  {
                    slug: scheme?.slug,
                  }
                )}`}
                variant="secondary"
                onClick={() =>
                  click("click_cta", {
                    copyCta: "Enquire about Purchasing",
                  })
                }
              >
                {t("cta.purchase-cta")}
                <IconActionsOpenInNew />
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
